/* eslint-disable complexity  -- complex*/
import type { RecaptchaV2Config } from '@vista-digital/occ-ui/src/lib/subcomponents/captcha/recaptcha-v2/types/config'
import type { OccContextProps } from '../cms/core-components/occConfiguration/occConfiguration.models'
import { baseApi } from './api'
import { getLogger } from './logger'
import type { LoggerConfiguration } from './logger/logger.types'
import type { ExtensionDef } from './models/extension-def'
import type { InitialDataResponse, OccApiProps } from './models/initialDataResponse'
import type { PageBase } from './models/pageBase'
import type { BasePagesEnum, BaseSubPageKey } from './models/pagelist-models'
import type { PageResponse } from './models/pageResponse'
import type { NavigationService } from './navigation.service'
import { Services } from './services'

export interface LoadPageModel<PagesEnum extends BasePagesEnum> {
    data: PageResponse
    friendlyUrl?: string
    page?: keyof PagesEnum
    id?: string
    usePageUrl?: boolean
    url?: string
}

export interface FilmExtensionsModel {
    seoPageTitle?: string
    htmlText?: string
    metaDescription?: string
    synopsis?: string
    displayAdvanceBookingPeriodNotification: boolean
    film: { filmPicker: { filterMode: number; options: string[] } }
}

export interface ConcessionCinemaCheck {
    siteId: string
}

export interface InitializeResult {
    occConfiguration: OccContextProps
    occApi: OccApiProps
    gtmId: string
    rumId: string
    recaptcha: RecaptchaV2Config
    loggingConfiguration: LoggerConfiguration
    country: string
}

export class CmsService<InitialDataR extends InitialDataResponse, PagesEnum extends BasePagesEnum> {
    private data: InitialDataR
    private navService: NavigationService<PagesEnum, BaseSubPageKey<PagesEnum>>

    public constructor(navigationService: NavigationService<PagesEnum, BaseSubPageKey<PagesEnum>>) {
        this.navService = navigationService
    }

    public loadPageComponents = async <T extends PageResponse>(model: LoadPageModel<PagesEnum>): Promise<T> => {
        const componentsNames: string[] = []
        if (model.data) {
            for (const key in model.data.components) {
                componentsNames.push(key)
            }
        }

        const ancestorComponents: string[] = []
        if (model.data) {
            for (const key in model.data.ancestorComponents) {
                for (const componentKey in model.data.ancestorComponents[key]) {
                    ancestorComponents.push(key + '.' + componentKey)
                }
            }
        }

        let friendlyUrl =
            model.friendlyUrl ?? (Services.NavigationService.getLocation()?.pathname || window.location.pathname)
        let extraQP = ''
        if (model.page && this.navService.Pages[model.page]?.parent && this.navService.Pages[model.page]?.customAlias) {
            friendlyUrl = this.navService.Pages[this.navService.Pages[model.page].parent!].urls![0]
            extraQP = '&descendantOfType=' + this.navService.Pages[model.page].customAlias
        }

        // Used for the 404 page which can be on any url
        if (model.usePageUrl && model.page) friendlyUrl = this.data.pages[model.page.toString()]![0]

        // Used for getting regular cinema page data for B2B cinema pages
        if (model.url) friendlyUrl = model.url

        // Make a request, populate the fields
        let pageData: T | undefined
        try {
            const queryKey =
                friendlyUrl +
                this.buildQueryArray('components', componentsNames) +
                this.buildQueryArray('ancestorComponents', ancestorComponents) +
                extraQP

            const rootPageData = window.pageData?.find(p => p.key === queryKey)
            if (rootPageData) {
                pageData = rootPageData.value as T
            } else pageData = await baseApi.GET<T>(baseApi.Page + '?friendly=' + queryKey)

            // Detect a 404
            if (!pageData || ((!pageData.nodeId || !pageData.alias) && pageData['status'] === 404)) {
                throw new Error('Seems like a 404')
            }
        } catch (error: unknown) {
            getLogger('CmsService').value.error('Failed to load page components', { error, friendlyUrl, extraQP })
            Services.NavigationService.goToPage('ErrorNotFound')
        }

        if (model.data && pageData) {
            if (!pageData.components) pageData.components = model.data.components
            else {
                componentsNames.forEach(name => {
                    if (!pageData?.components[name]) pageData!.components[name] = model.data.components[name]
                })
            }
        }

        if (pageData?.redirectDetailsElement) {
            const enabled = pageData?.redirectDetailsElement?.redirect
            const url = pageData?.redirectDetailsElement?.url?.url

            if (enabled && url) {
                Services.NavigationService.goTo(url)
            }
        }

        setTimeout(() => {
            // To properly render all occs
            window.dispatchEvent(new Event('resize'))
        }, 100)

        return pageData as T
    }

    public initialize = async (): Promise<InitializeResult> => {
        const pages = Services.NavigationService.PagesNames(true)
        this.data = (window.initialData ?? (await baseApi.GET<InitialDataR>(baseApi.InitialData))) as InitialDataR

        Services.CmsDataService.set(this.data)

        const mapParams = (u: string, prms?: string[]) => u + prms?.map(p => ':' + p + '/')?.join('')

        for (const page of pages) {
            const cmsPage = Services.NavigationService.Pages[page]
            if (!cmsPage) continue

            const parentUrls = this.data.pages[cmsPage.parent!]
            const pageUrls = this.data.pages[page]

            cmsPage.urls = cmsPage.parent ? parentUrls?.map(p => mapParams(p, cmsPage.params)) : pageUrls

            if (!cmsPage.subPages?.length) continue

            for (const subPage of cmsPage.subPages) {
                const subPageUrls = this.data.pages[subPage.alias!]
                const prms = subPage.params
                if (!subPageUrls) continue

                subPage.urls = prms?.length ? subPageUrls.map(p => mapParams(p, prms)) : subPageUrls
            }
        }

        return {
            occConfiguration: this.data.occConfiguration,
            occApi: this.data.api,
            gtmId: this.data.gtmId,
            rumId: this.data.rumId,
            recaptcha: this.data.captcha,
            loggingConfiguration: this.data.loggingConfiguration,
            country: this.data.country,
        }
    }

    public loadPageList = async <T extends PageBase>(properties: string[], pageUrl?: string): Promise<T[]> => {
        const data = await baseApi.GET<T[]>(
            baseApi.PageList +
                '?friendly=' +
                (pageUrl ?? (Services.NavigationService.getLocation()?.pathname || window.location.pathname)) +
                this.buildQueryArray('properties', properties)
        )

        return data ?? []
    }

    public listSubPages = async <T extends PageBase>(page: keyof PagesEnum, properties: string[]): Promise<T[]> => {
        const data = await baseApi.GET<T[]>(
            baseApi.PageList +
                '?friendly=' +
                this.navService.Pages[page].urls![0] +
                this.buildQueryArray('properties', properties)
        )

        return data ?? []
    }

    public loadConfigurationComponents = async <T>(model: { data: T; friendlyUrl?: string }): Promise<T> => {
        const componentsNames: string[] = []
        if (model.data) {
            for (const key in model.data) {
                componentsNames.push(key)
            }
        }

        const friendlyUrl =
            model.friendlyUrl || Services.NavigationService.getLocation()?.pathname || window.location.pathname
        const pageData = await baseApi.GET<T>(
            baseApi.Configuration + '?friendly=' + friendlyUrl + this.buildQueryArray('components', componentsNames)
        )

        if (pageData) {
            for (const component of componentsNames) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- any allowed
                if (pageData[component]) model.data[component] = pageData[component]
            }
        }

        return model.data
    }

    public getExtensions = async <T>(extension: ExtensionDef, urlParameter = ''): Promise<T[]> => {
        const dataPromise = baseApi.GET(baseApi.Extensions + '/' + extension.name + '/' + urlParameter)
        const attributesPromise = extension.mapAttributes
            ? baseApi.GET(baseApi.Attributes(extension.mapAttributes))
            : Promise.resolve(null)

        const promise = await Promise.all([dataPromise, attributesPromise])

        return extension.mapper!(promise[0], promise[1]) as T[]
    }

    private buildQueryArray = (queryName: string, list: string[]) => {
        if (list && list.length) {
            return list.map(e => `&${queryName}=${e}`).join('')
        }
        return ''
    }
}
